import { useEffect, useState } from 'react';
import { Save, ChevronDown, ChevronRight, Globe } from 'lucide-react';
import type { Lang, PageKey } from '../../types/cms';
import type { AdminCommon } from '../../pages/Admin';

const LANGS: { code: Lang; label: string }[] = [
  { code: 'tc', label: '繁體中文' },
  { code: 'sc', label: '简体中文' },
  { code: 'en', label: 'English' },
];

const PAGES: { key: PageKey; label: string }[] = [
  { key: 'home', label: '首頁 /' },
  { key: 'services', label: '服務 /services' },
  { key: 'consultants', label: '顧問團隊 /consultants' },
  { key: 'about', label: '關於我們 /about' },
  { key: 'faq', label: '常見問題 /faq' },
  { key: 'contact', label: '聯繫我們 /contact' },
];

const SEO_FIELDS: { key: 'title' | 'description' | 'keywords'; label: string; rows?: number }[] = [
  { key: 'title', label: 'Title 標題' },
  { key: 'description', label: 'Meta Description', rows: 3 },
  { key: 'keywords', label: 'Keywords', rows: 2 },
];

export default function SeoManager({ draft, onSave }: AdminCommon) {
  const [site, setSite] = useState(draft.site);
  const [pages, setPages] = useState(draft.seo?.pages);
  const [open, setOpen] = useState<PageKey | null>('home');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSite(draft.site);
    setPages(draft.seo?.pages);
  }, [draft]);

  const patchPage = (page: PageKey, field: string, lang: Lang, value: string) =>
    setPages(
      (prev: any) => ({
        ...prev,
        [page]: { ...prev?.[page], [field]: { ...prev?.[page]?.[field], [lang]: value } },
      })
    );

  const saveAll = async () => {
    setSaving(true);
    try {
      await onSave({ site, seo: { pages } });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Site settings */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5" /> 網站設定
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">公司名稱（繁體）</label>
            <input
              className="w-full border border-gray-200 rounded p-2 text-sm"
              value={site?.orgNameTc || ''}
              onChange={(e) => setSite({ ...site, orgNameTc: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">公司名稱（英文）</label>
            <input
              className="w-full border border-gray-200 rounded p-2 text-sm"
              value={site?.orgNameEn || ''}
              onChange={(e) => setSite({ ...site, orgNameEn: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">正式網址（影響 sitemap / llms / canonical）</label>
            <input
              className="w-full border border-gray-200 rounded p-2 text-sm"
              value={site?.siteUrl || ''}
              placeholder="https://www.example.com"
              onChange={(e) => setSite({ ...site, siteUrl: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">
            WhatsApp 號碼（國際格式毋須 + 號，如 85262322466；留空 = 隱藏全站 WhatsApp 按鈕）
          </label>
          <input
            className="w-full border border-gray-200 rounded p-2 text-sm md:w-1/2"
            value={site?.whatsappNumber || ''}
            placeholder="852XXXXXXXX"
            onChange={(e) => setSite({ ...site, whatsappNumber: e.target.value.replace(/[^\d]/g, '') })}
          />
        </div>
      </div>

      {/* Per-page SEO */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-navy-900">頁面 SEO</h2>
          <button
            onClick={saveAll}
            disabled={saving}
            className="flex items-center gap-1 text-sm px-4 py-2 rounded bg-gold-500 text-navy-900 font-bold hover:bg-gold-600 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? '儲存中…' : '儲存全部到草稿'}
          </button>
        </div>

        <div className="space-y-2">
          {PAGES.map(({ key, label }) => {
            const isOpen = open === key;
            return (
              <div key={key} className="border rounded-lg">
                <button
                  onClick={() => setOpen(isOpen ? null : key)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
                >
                  <span className="text-sm font-semibold text-gray-800">{label}</span>
                  {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-4">
                    {SEO_FIELDS.map(({ key: field, label: fieldLabel, rows }) => (
                      <div key={field}>
                        <p className="text-xs text-gray-400 mb-1.5">{fieldLabel}</p>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                          {LANGS.map(({ code, label: langLabel }) => {
                            const page = pages as any;
                            return (
                              <div key={code}>
                                <label className="block text-[11px] text-gray-400 mb-1">{langLabel}</label>
                                {rows ? (
                                  <textarea
                                    rows={rows}
                                    className="w-full border border-gray-200 rounded p-2 text-sm"
                                    value={page?.[key]?.[field]?.[code] || ''}
                                    onChange={(e) => patchPage(key, field, code, e.target.value)}
                                  />
                                ) : (
                                  <input
                                    className="w-full border border-gray-200 rounded p-2 text-sm"
                                    value={page?.[key]?.[field]?.[code] || ''}
                                    onChange={(e) => patchPage(key, field, code, e.target.value)}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
